import { useEffect, useState } from "react";
import { Repeat, Pause, Play, Square, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtIt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const STATUS = {
  active: ["Attiva", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
  paused: ["In pausa", "bg-amber-500/15 text-amber-400 border-amber-500/30"],
  ended: ["Terminata", "bg-slate-500/15 text-slate-300 border-slate-500/30"],
};

function Next({ iso }) {
  const [left, setLeft] = useState(() => new Date(iso) - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(iso) - Date.now()), 30000);
    return () => clearInterval(t);
  }, [iso]);
  if (left <= 0) return <span className="text-amber-400">in avvio…</span>;
  const s = Math.floor(left / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return <span className="font-mono-code">{d ? `${d}g ` : ""}{h}h {m}m</span>;
}

export default function RecurrenceCard({ r, editions, onChange }) {
  const [label, cls] = STATUS[r.status] || STATUS.active;
  const filters = Object.values(r.filter_labels || {}).filter(Boolean);
  const act = async (action, msg) => {
    if (action === "end" && !window.confirm("Terminare definitivamente la ricorrenza?")) return;
    try {
      await api.recurrenceAction(r.id, action);
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    }
  };
  return (
    <Card className="p-4 bg-card border-fuchsia-500/30 border" data-testid={`recurrence-row-${r.id}`}>
      <div className="flex items-center gap-2">
        <Repeat className="h-4 w-4 text-fuchsia-400" />
        <h4 className="font-semibold flex-1 truncate">{r.name}</h4>
        <Badge className="border bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30">{r.frequency === "monthly" ? "Ogni mese" : "Ogni settimana"} · {r.send_time}</Badge>
        <Badge className={`border ${cls}`} data-testid={`recurrence-status-${r.id}`}>{label}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{r.contest_name}{filters.length ? ` · ${filters.join(" · ")}` : ""}{r.end_date ? ` · fino al ${new Date(r.end_date).toLocaleDateString("it-IT")}` : ""} · solo nuovi contatti</p>
      <p className="text-xs mt-2 whitespace-pre-wrap line-clamp-2">{r.preview_text}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs rounded-md bg-secondary/40 px-3 py-2">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        {r.status === "active" ? <span data-testid={`recurrence-next-${r.id}`}>Prossimo invio: <b>{fmtIt(r.next_run_at)}</b> · tra <Next iso={r.next_run_at} /></span> : <span>Nessun invio programmato</span>}
        <span className="ml-auto" data-testid={`recurrence-editions-${r.id}`}><b>{r.editions || 0}</b> edizioni · {editions.reduce((a, c) => a + (c.sent || 0), 0)} messaggi</span>
      </div>
      {r.status !== "ended" && (
        <div className="mt-2 flex gap-1 justify-end">
          {r.status === "active" ? (
            <Button size="sm" variant="secondary" className="h-7 text-xs gap-1" onClick={() => act("pause", "Ricorrenza in pausa")} data-testid={`recurrence-pause-${r.id}`}><Pause className="h-3 w-3" /> Pausa</Button>
          ) : (
            <Button size="sm" variant="secondary" className="h-7 text-xs gap-1" onClick={() => act("resume", "Ricorrenza ripresa")} data-testid={`recurrence-resume-${r.id}`}><Play className="h-3 w-3" /> Riprendi</Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-400" onClick={() => act("end", "Ricorrenza terminata")} data-testid={`recurrence-end-${r.id}`}><Square className="h-3 w-3" /> Termina</Button>
        </div>
      )}
    </Card>
  );
}
